import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import PageWrapper from '../components/PageWrapper';
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import Button from '../components/Button';
import BlockTitle from '../components/BlockTitle';
import AddInvestmentModal from '../components/AddInvestmentModal';
import EditInvestmentModal from '../components/EditInvestmentModal';
import GenerateZawmrpModal from '../components/GenerateZawmrpModal';
import CardSkeleton from '../components/CardSkeleton';
import { useInvestment } from '../context/InvestmentContext';

// Liczba ZAMKNIETYCH etapow. `current_stage` to etap W TOKU, wiec po zamknieciu
// ostatniego wynosi N+1 i ekran pokazywal "10 / 9" (Kutno A, 04.11.2026).
// Ta sama regula co `stages_completed` w sovoPayInvoicesApi.
const etapyZamkniete = (inv) => {
  const wszystkie = Number(inv.stages_total) || 0;
  const wToku = Number(inv.current_stage) || 0;
  // "Zakonczona" liczymy jako wszystkie etapy tylko, gdy licznik to potwierdza:
  // Gwiazdowo F i Rokietnica C maja completed=true przy 8/9 i 7/9.
  if (inv.completed && wszystkie && wToku >= wszystkie) return wszystkie;
  const zHistorii = Math.max(0, ...(inv.stageCompletionHistory || []).map((e) => Number(e.stageNumber) || 0));
  return Math.max(0, Math.min(wszystkie || Infinity, Math.max(wToku - 1, zHistorii)));
};

const Investments = () => {
  const navigate = useNavigate();
  const { selectedInvestmentId, setSelectedInvestment, refreshInvestments } = useInvestment();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editInvestmentId, setEditInvestmentId] = useState(null);
  const [dfgInvestment, setDfgInvestment] = useState(null);

  const fetchInvestments = async () => {
    setLoading(true);
    try {
      const investmentsSnapshot = await getDocs(collection(firestore, 'investments'));
      const investmentsList = await Promise.all(
        investmentsSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          // Fetch flats count for this investment
          const flatsSnapshot = await getDocs(collection(firestore, `investments/${doc.id}/flats`));
          const flatsCount = flatsSnapshot.size;
          const soldFlats = flatsSnapshot.docs.filter(f => f.data().status === 'sold').length;

          return {
            id: doc.id,
            ...data,
            totalFlats: flatsCount,
            soldFlats: soldFlats,
          };
        })
      );
      setInvestments(investmentsList);
    } catch (error) {
      console.error('Error fetching investments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, []);

  const handleSelectInvestment = (id) => {
    setSelectedInvestment(id);
    navigate('/customers');
  };

  const handleEditInvestment = (id, e) => {
    e.stopPropagation();
    setEditInvestmentId(id);
    setIsEditModalOpen(true);
  };

  const handleAddSave = async (newId) => {
    await fetchInvestments();
    await refreshInvestments();
    setSelectedInvestment(newId);
  };

  const handleEditSave = async () => {
    await fetchInvestments();
    await refreshInvestments();
  };

  const getStatusBadge = (investment) => {
    if (investment.completed) {
      return (
        <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Zakonczona
        </span>
      );
    }
    if (investment.status === 'active') {
      return (
        <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          Aktywna
        </span>
      );
    }
    return (
      <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
        {investment.status || 'Nieznany'}
      </span>
    );
  };

  return (
    <PageWrapper>
      <div className="flex justify-between items-center">
        <PageTitle>Inwestycje</PageTitle>
        <Button variant="default" onClick={() => setIsAddModalOpen(true)}>
          + Dodaj inwestycje
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </>
        ) : investments.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500 mb-4">Brak inwestycji</p>
            <Button variant="default" onClick={() => setIsAddModalOpen(true)}>
              Dodaj pierwsza inwestycje
            </Button>
          </div>
        ) : (
          investments.map((investment) => (
            <Card
              key={investment.id}
              className={`p-6 cursor-pointer transition-all hover:shadow-lg ${
                selectedInvestmentId === investment.id
                  ? 'ring-2 ring-[var(--maroon)] bg-[var(--maroon)]/5'
                  : ''
              }`}
              onClick={() => handleSelectInvestment(investment.id)}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <BlockTitle className="text-lg">{investment.name}</BlockTitle>
                  <p className="text-sm text-gray-500 mt-1">{investment.location}</p>
                </div>
                {getStatusBadge(investment)}
              </div>

              {investment.manager && (
                <div className="mb-4 text-sm">
                  <p className="text-gray-600">
                    <span className="font-medium">Kierownik:</span> {investment.manager.name}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Tel:</span> {investment.manager.phone}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Etap budowy</span>
                  <span className="font-medium">
                    {etapyZamkniete(investment)} / {investment.stages_total || 1}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[var(--maroon)] h-2 rounded-full transition-all"
                    style={{
                      width: `${
                        (etapyZamkniete(investment) / (investment.stages_total || 1)) * 100
                      }%`,
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-between text-sm mb-4">
                <div>
                  <span className="text-gray-600">Lokale: </span>
                  <span className="font-medium">{investment.totalFlats || 0}</span>
                </div>
                <div>
                  <span className="text-gray-600">Sprzedane: </span>
                  <span className="font-medium">{investment.soldFlats || 0}</span>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant={selectedInvestmentId === investment.id ? 'default' : 'bordered'}
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectInvestment(investment.id);
                  }}
                >
                  {selectedInvestmentId === investment.id ? 'Wybrana' : 'Wybierz'}
                </Button>
                <Button
                  variant="bordered"
                  onClick={(e) => handleEditInvestment(investment.id, e)}
                >
                  Edytuj
                </Button>
                <Button
                  variant="bordered"
                  title="Wygeneruj zgłoszenie ZAWMRP do DFG"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDfgInvestment(investment);
                  }}
                >
                  DFG
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      <AddInvestmentModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddSave}
      />

      <EditInvestmentModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditInvestmentId(null);
        }}
        investmentId={editInvestmentId}
        onSave={handleEditSave}
      />

      <GenerateZawmrpModal
        isOpen={!!dfgInvestment}
        onClose={() => {
          setDfgInvestment(null);
          fetchInvestments();
        }}
        investmentId={dfgInvestment?.id}
        investment={dfgInvestment}
      />
    </PageWrapper>
  );
};

export default Investments;
